/**
 * PWA plumbing — the permanent fix for "Chrome's URL bar is eating my
 * meeting" and "Back doesn't give me a floating window".
 *
 * An installed PWA is the only way a website gets:
 *   • no browser address bar at all (display: standalone)
 *   • Chrome's automatic Picture-in-Picture when the app is backgrounded
 *   • an app icon on the home screen (still zero Play Store install)
 *
 * Until the student installs it, we fall back to the Fullscreen API, which
 * hides the URL bar for the session as long as we ask during a real tap.
 */

let deferredPrompt = null;
const listeners = new Set();

const notify = () => listeners.forEach((fn) => fn(canInstall()));

export const isStandalone = () =>
  typeof window !== "undefined" &&
  (window.matchMedia?.("(display-mode: standalone)")?.matches ||
    window.matchMedia?.("(display-mode: fullscreen)")?.matches ||
    window.navigator.standalone === true);

export const canInstall = () => !!deferredPrompt && !isStandalone();

export function onInstallabilityChange(fn) {
  listeners.add(fn);
  fn(canInstall());
  return () => listeners.delete(fn);
}

export function initPwa() {
  if (typeof window === "undefined") return;
  window.addEventListener("beforeinstallprompt", (e) => {
    e.preventDefault();
    deferredPrompt = e;
    notify();
  });
  window.addEventListener("appinstalled", () => {
    deferredPrompt = null;
    notify();
  });
  if ("serviceWorker" in navigator) {
    window.addEventListener("load", () => {
      navigator.serviceWorker.register("/sw.js").catch(() => {});
    });
  }
}

export async function promptInstall() {
  if (!deferredPrompt) return "unavailable";
  const p = deferredPrompt;
  deferredPrompt = null;
  notify();
  try {
    p.prompt();
    const { outcome } = await p.userChoice;
    return outcome; // "accepted" | "dismissed"
  } catch (_) {
    return "failed";
  }
}

/**
 * Fullscreen exists to kill the mobile browser's address bar. On a desktop
 * with a mouse it is pure annoyance (the host has to press Esc), so we never
 * ask for it there.
 */
export const isFullscreenEligible = () => {
  if (typeof window === "undefined") return false;
  if (isStandalone()) return false;
  const coarse = window.matchMedia?.("(pointer: coarse)")?.matches;
  const touch = "ontouchstart" in window || (navigator.maxTouchPoints || 0) > 1;
  const small = window.innerWidth < 1024;
  return !!((coarse || touch) && small);
};

/** Ask for real fullscreen. MUST be called inside a user gesture. */
export async function goFullscreenNow(force = false) {
  if (isStandalone()) return true;
  if (!force && !isFullscreenEligible()) return false;
  const el = document.documentElement;
  try {
    if (document.fullscreenElement) return true;
    const req = el.requestFullscreen || el.webkitRequestFullscreen;
    if (!req) return false;
    await req.call(el, { navigationUI: "hide" });
    return true;
  } catch (_) {
    return false;
  }
}

export const isFullscreen = () =>
  typeof document !== "undefined" && !!(document.fullscreenElement || document.webkitFullscreenElement);

/** Keeps the phone screen awake for the length of the class. */
export async function requestWakeLock() {
  try {
    if (!("wakeLock" in navigator)) return null;
    const lock = await navigator.wakeLock.request("screen");
    return lock;
  } catch (_) {
    return null;
  }
}
